import type { ScreenHintPayload } from '../../api/agent';
import type { OpenStream } from '../../api/client';

export interface AgentStreamEvent {
  event: string;
  data: unknown;
}

interface AgentStreamRequest {
  threadId: string;
  message?: string;
  resume?: string;
  screen?: ScreenHintPayload;
  abortSignal?: AbortSignal;
  resumeRunId?: string;
  since?: number;
}

interface AgentStreamDeps {
  openStream: OpenStream;
}

export async function* parseSseStream(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<AgentStreamEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let separator = findSeparator(buffer);
      while (separator) {
        const raw = buffer.slice(0, separator.index);
        buffer = buffer.slice(separator.index + separator.length);

        const parsed = parseEvent(raw);
        if (parsed) yield parsed;

        separator = findSeparator(buffer);
      }
    }

    const tail = parseEvent(buffer);
    if (tail) yield tail;
  } finally {
    reader.releaseLock();
  }
}

function findSeparator(buffer: string): { index: number; length: number } | null {
  const lf = buffer.indexOf('\n\n');
  const crlf = buffer.indexOf('\r\n\r\n');

  if (lf === -1 && crlf === -1) return null;
  if (crlf !== -1 && (lf === -1 || crlf < lf)) return { index: crlf, length: 4 };
  return { index: lf, length: 2 };
}

function parseEvent(raw: string): AgentStreamEvent | null {
  if (!raw.trim()) return null;

  let event = 'message';
  const dataLines: string[] = [];

  for (const line of raw.split(/\r?\n/)) {
    if (line.startsWith(':')) continue;

    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);

    const rawValue = colon === -1 ? '' : line.slice(colon + 1);
    const value = rawValue.startsWith(' ') ? rawValue.slice(1) : rawValue;

    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
  }

  if (dataLines.length === 0) return null;

  const payload = dataLines.join('\n');
  try {
    return { event, data: JSON.parse(payload) };
  } catch {
    return { event, data: payload };
  }
}

export async function* streamAgentTurn(
  deps: AgentStreamDeps,
  request: AgentStreamRequest,
): AsyncGenerator<AgentStreamEvent> {
  const body = await deps.openStream(
    `/ai/assistant/threads/${request.threadId}/stream`,
    {
      message: request.message,
      resume: request.resume,
      screen: request.screen,
      resumeRunId: request.resumeRunId,
      since: request.since,
    },
    request.abortSignal,
  );

  yield* parseSseStream(body);
}

export async function* streamAgentTurnWithReattach(
  deps: AgentStreamDeps,
  request: AgentStreamRequest,
  options: { maxReattachments?: number } = {},
): AsyncGenerator<AgentStreamEvent> {
  const maxReattachments = options.maxReattachments ?? 2;

  let runId: string | undefined;
  let seen = 0;
  let attempt = 0;

  for (;;) {
    const call: AgentStreamRequest =
      runId === undefined
        ? request
        : {
            threadId: request.threadId,
            resumeRunId: runId,
            since: seen,
            abortSignal: request.abortSignal,
          };

    try {
      for await (const event of streamAgentTurn(deps, call)) {
        if (event.event !== 'metadata') seen += 1;

        if (event.event === 'metadata') {
          const data = event.data as { runId?: string; done?: boolean } | undefined;
          if (data?.runId) runId = data.runId;
          if (data?.done) {
            yield event;
            return;
          }
        }

        yield event;
      }
      return;
    } catch (error) {
      if (request.abortSignal?.aborted) return;
      if (!runId || attempt >= maxReattachments) throw error;
      attempt += 1;
    }
  }
}
